/**
 * Booking-widget health watcher. The Hospitable loader injects
 * #booking-iframe asynchronously; this reports slow/failed loads so guests
 * aren't staring at blank space. Clock/timer/DOM are injected for testability.
 *
 * Deadlines are measured against a real clock, not tick counts: background
 * tabs clamp setTimeout to >=1s, so counting ticks would stretch a 20s
 * deadline well past 20s.
 */
export interface WidgetCallbacks {
  isReady: () => boolean;
  onSlow: () => void;
  onFailed: () => void;
  onReady: () => void;
}

export const SLOW_AFTER_MS = 4000;
export const FAILED_AFTER_MS = 20000;
export const POLL_MS = 500;

export function watchWidget(
  cb: WidgetCallbacks,
  setTimer: (ms: number, fn: () => void) => void,
  now: () => number = () => Date.now(),
): void {
  const startedAt = now();
  let slowFired = false;

  const tick = (): void => {
    if (cb.isReady()) {
      cb.onReady();
      return;
    }
    const elapsed = now() - startedAt;
    if (elapsed >= FAILED_AFTER_MS) {
      cb.onFailed();
      return;
    }
    if (!slowFired && elapsed >= SLOW_AFTER_MS) {
      slowFired = true;
      cb.onSlow();
    }
    setTimer(POLL_MS, tick);
  };

  setTimer(POLL_MS, tick);
}
